import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import styles from './SignUp.module.css';
import BrandLogo from './BrandLogo';
import loadingStyles from './shared/Loading.module.css';
import config, { logger } from '../config';
import { checkGoogleUserExists, getApiHeaders, tryUpdateUserName } from '../utils/api';
import { identifyUser, capture, SIGNUP_COMPLETED_EVENT } from '../analytics/posthog';
import { requestGoogleSession, submitGoogleLogin, handleFacebookLogin } from '../utils/socialLogin';
import { getFirstName, persistAuthUser, rememberSignupCollectedName, validatePasswordStrength, getStrengthColor, getStrengthText, getRequirementColor, getStrengthGuidance } from '../utils/helper';
import OnboardingInitial from './OnboardingInitial';
import ExtendedWelcomeFlow, { readExtendedWelcomeBlocker } from './ExtendedWelcomeFlow/ExtendedWelcomeFlow';

function clearSignupFlow() {
  try {
    sessionStorage.removeItem(config.STORAGE_KEYS.SIGNUP_ONBOARDING_FLOW);
  } catch {
    /* ignore private mode */
  }
}

const SignUp = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [phase, setPhase] = useState('name');
  const [collectedName, setCollectedName] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);

  useEffect(() => {
    clearSignupFlow();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase]);

  const handleNameContinue = (name) => {
    setCollectedName(name);
    setFormData((prev) => ({ ...prev, fullName: name }));
    setPhase('welcome');
  };

  const handleWelcomeCommitted = () => {
    setPhase('form');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'fullName') setCollectedName(value);
    // Clear error when user starts typing
    if (error) setError('');

    // Check password strength when password field changes
    if (name === 'password') {
      setPasswordStrength(validatePasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const extendedWelcomeBlocker = readExtendedWelcomeBlocker();
    if (!extendedWelcomeBlocker) {
      setPhase('welcome');
      setError('Choose what’s holding you back before creating an account.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.SIGNUP}`, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...formData,
          extended_welcome_blocker: extendedWelcomeBlocker,
        })
      });

      const data = await response.json();

      // Check for success response
      if (data.success) {
        capture(posthog, SIGNUP_COMPLETED_EVENT, { method: 'email' });
        rememberSignupCollectedName(formData.fullName);
        clearSignupFlow();
        const email = data.data?.email || formData.email;
        navigate(`/verify?email=${encodeURIComponent(email)}`);
        return;
      }

      // Handle validation errors
      if (data.validation_errors && Array.isArray(data.validation_errors)) {
        const validationErrorMessages = data.validation_errors.join('\n');
        setError(validationErrorMessages);
        return;
      }

      // Handle other errors
      setError(data.message || 'Signup failed. Please try again.');

    } catch (err) {
      logger.error('Signup error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLoginClick = async (provider) => {
    setSocialLoading(provider);
    setError('');

    try {
      let result;
      let openAgent = false;
      const confirmedName = formData.fullName.trim();

      switch (provider) {
        case 'Google': {
          const session = await requestGoogleSession();
          const existsResult = await checkGoogleUserExists(session.accessToken);
          if (!existsResult.ok) {
            throw new Error(existsResult.message || 'Could not check this account.');
          }
          if (!existsResult.exists) {
            openAgent = true;
            result = await submitGoogleLogin({
              ...session,
              fullName: confirmedName || session.fullName,
              extendedWelcomeBlocker: readExtendedWelcomeBlocker(),
            });
          } else {
            result = await submitGoogleLogin(session);
          }
          break;
        }
        case 'Facebook':
          result = await handleFacebookLogin();
          break;
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }

      if (result.success) {
        if (result.data && result.data.token) {
          const accountName = openAgent
            ? (confirmedName || result.data.fullName)
            : result.data.fullName;
          if (
            openAgent
            && confirmedName
            && confirmedName !== (result.data.fullName || '').trim()
          ) {
            await tryUpdateUserName(result.data.token, confirmedName);
          }
          persistAuthUser({
            token: result.data.token,
            name: accountName,
            email: result.data.email,
          });
          identifyUser(posthog, result.data.token, {
            name: accountName,
            email: result.data.email,
          });
          capture(posthog, SIGNUP_COMPLETED_EVENT, { method: provider.toLowerCase() });
          if (openAgent && accountName) {
            rememberSignupCollectedName(accountName);
            clearSignupFlow();
          }
        }
        navigate(openAgent ? '/v2-onboarding-agent' : '/home');
      } else {
        throw new Error(result.message || `${provider} signup failed`);
      }
    } catch (err) {
      logger.error(`${provider} signup error:`, err);
      // Handle different types of error objects
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`${provider} signup failed: ${errorMessage}`);
    } finally {
      setSocialLoading('');
    }
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    navigate('/login');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const passwordDescribedBy = [
    error && 'signup-error',
    formData.password && passwordStrength && 'signup-strength'
  ].filter(Boolean).join(' ') || undefined;

  if (phase === 'name') {
    return (
      <OnboardingInitial
        initialName={collectedName}
        onContinue={handleNameContinue}
        onBack={() => navigate('/')}
      />
    );
  }

  if (phase === 'welcome') {
    return (
      <ExtendedWelcomeFlow
        entry="signup"
        firstName={getFirstName(collectedName)}
        onCommitted={handleWelcomeCommitted}
        onExit={() => setPhase('name')}
      />
    );
  }

  return (
    <main className={styles['signup-container']} id="signup-main" aria-label="Create account">
      <div className={styles['signup-content']}>
        <div className={styles['logo-wrap']}>
          <BrandLogo alt="Richuel Logo" />
        </div>

        <div className={styles['signup-form-container']}>
          <h1 className={styles['signup-title']}>Create Account</h1>

          <div className={styles['login-link']}>
            <p>Already have an account? <a href="/login" onClick={handleLoginClick}>Login</a></p>
          </div>

          <div className={styles['social-login']} role="group" aria-label="Sign up with social account">
            <button
              type="button"
              className={`${styles['social-button']} ${styles['google']}`}
              onClick={() => handleSocialLoginClick('Google')}
              disabled={socialLoading === 'Google'}
              aria-label={socialLoading === 'Google' ? 'Signing up with Google…' : 'Sign up with Google'}
            >
              {socialLoading === 'Google' ? (
                <div className={loadingStyles['loading-spinner']}>
                  <div className={loadingStyles['spinner-ring']}></div>
                  <div className={loadingStyles['spinner-ring']}></div>
                  <div className={loadingStyles['spinner-ring']}></div>
                </div>
              ) : (
                <svg className={styles['google-icon']} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`${styles['social-button']} ${styles['facebook']}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled
              aria-label="Facebook sign up (currently unavailable)"
            >
              <svg className={styles['facebook-icon']} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
          </div>

          {error && (
            <div key={error} id="signup-error" className={styles['error-message']} role="alert" aria-live="polite">
              {error.split('\n').map((errorLine, index) => (
                <div key={index}>{errorLine}</div>
              ))}
            </div>
          )}

          <form className={styles['signup-form']} onSubmit={handleSubmit} aria-label="Create account with email">
            <div className={styles['form-group']}>
              <label htmlFor="signup-fullName">Full Name</label>
              <input
                type="text"
                id="signup-fullName"
                name="fullName"
                placeholder="Enter here"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="name"
                aria-describedby={error ? 'signup-error' : undefined}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="signup-phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="signup-phoneNumber"
                name="phoneNumber"
                placeholder="Enter here"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="tel"
                aria-describedby={error ? 'signup-error' : undefined}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="signup-email">Email</label>
              <input
                type="email"
                id="signup-email"
                name="email"
                placeholder="Enter here"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="email"
                aria-describedby={error ? 'signup-error' : undefined}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="signup-password">Create Password</label>
              <div className={styles['password-input-container']}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="signup-password"
                  name="password"
                  placeholder="Enter here"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  aria-describedby={passwordDescribedBy}
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                    {showPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    )}
                  </svg>
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.password && passwordStrength && (
                <div id="signup-strength" className={styles['password-strength']} role="status" aria-live="polite">
                  <div className={styles['strength-text']} style={{ color: getStrengthColor(passwordStrength.strength) }}>
                    {getStrengthText(passwordStrength.strength)}
                  </div>
                  <div className={styles['strength-bar']}>
                    <div
                      className={styles['strength-fill']}
                      style={{
                        width: `${passwordStrength.percentage}%`,
                        backgroundColor: getStrengthColor(passwordStrength.strength)
                      }}
                    ></div>
                  </div>
                  <div className={styles['strength-guidance']}>
                    {getStrengthGuidance(passwordStrength.strength)}
                  </div>
                  <div className={styles['strength-requirements']}>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.uppercase?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.uppercase?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.special?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.special?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.numeric?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.numeric?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.alpha?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.alpha?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.length?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.length?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.noSpaces?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.noSpaces?.text}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              className={styles['continue-button']}
              disabled={isLoading || passwordStrength?.strength !== 'very strong'}
              aria-busy={isLoading}
              aria-label={isLoading ? 'Creating account…' : 'Continue'}
            >
              {isLoading ? 'Creating Account...' : 'Continue'}
            </button>
          </form>

          <div className={styles['terms-text']}>
            <p>By clicking Sign up, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignUp;